import { BookOpenCheck, Files, FolderSearch } from 'lucide-react';
import { braccialiTestimonial } from '@/content/testimonials';
import type { SolutionContent } from './types';

export const commercialistiSolution: SolutionContent = {
  slug: 'commercialisti',
  navLabel: 'Commercialisti',
  navDescription: 'Quesiti fiscali e contenzioso tributario, con la prassi alla mano.',
  meta: {
    title: 'AI per commercialisti',
    description:
      'Quesiti fiscali, bilanci e operazioni societarie con normativa, prassi dell’Agenzia delle Entrate e giurisprudenza tributaria. Riferimenti verificabili.',
  },
  hero: {
    title: 'Trovanorma per commercialisti',
    subtitle:
      'Al tuo fianco quando arriva un quesito del cliente, un bilancio da leggere, una dichiarazione da verificare o un’operazione societaria da impostare.',
  },
  showcaseAudience: 'commercialisti',
  queryExamples: [
    {
      title: 'Indici di crisi dal bilancio',
      icon: 'Activity',
      question:
        'Allego il bilancio: calcola gli indici di allerta del Codice della crisi e dimmi quali sono sopra soglia.',
      attachment: 'bilancio-2025.xbrl',
      result: 'Foglio Excel con formule',
    },
    {
      title: 'Business plan a cinque anni',
      icon: 'TrendingUp',
      question:
        'Dal bilancio allegato sviluppa un business plan a **cinque anni**, con le ipotesi esplicitate riga per riga.',
      attachment: 'bilancio-2025.xlsx',
      result: 'Excel con proiezioni',
    },
    {
      title: 'Bozza di ricorso tributario',
      icon: 'Gavel',
      question:
        'Analizza l’avviso di accertamento: evidenzia i vizi e prepara la bozza di ricorso con la giurisprudenza a supporto.',
      attachment: 'avviso-accertamento.pdf',
      result: 'Bozza di ricorso in Word',
    },
    {
      title: 'Rivalutazione o plusvalenza?',
      icon: 'Percent',
      question:
        'Quota di S.r.l.: costo fiscale **30.000 €**, perizia **500.000 €**. Conviene rivalutare o pagare l’imposta sulla plusvalenza? Mettimi il confronto in un foglio.',
      result: 'Foglio Excel comparativo',
    },
    {
      title: 'Registrazione bolla doganale',
      icon: 'Ship',
      question:
        'Import dalla Cina con resa FOB: allego bolla e fattura, dammi le scritture contabili e che cosa finisce in dichiarazione IVA.',
      attachment: 'bolla-doganale.pdf',
      result: 'Scritture e adempimenti',
    },
    {
      title: 'Holding e PEX',
      icon: 'Building2',
      question:
        'Vorrei una holding per i dividendi delle partecipate: requisiti per la participation exemption, quando si applica e quale struttura consiglieresti.',
      result: 'Parere documentato',
    },
  ],
  tasks: [
    {
      icon: BookOpenCheck,
      title: 'Quesiti fiscali e tributari',
      description:
        'Approfondisci IVA, imposte dirette, agevolazioni, adempimenti e contenzioso mettendo insieme normativa, prassi dell’Agenzia delle Entrate e giurisprudenza tributaria.',
    },
    {
      icon: FolderSearch,
      title: 'Bilanci, dichiarazioni e documenti',
      description:
        'Carica bilanci XBRL, dichiarazioni, visure, prospetti e altri documenti del cliente per analizzare dati, verificare elementi rilevanti e approfondirli nel loro contesto normativo.',
    },
    {
      icon: Files,
      title: 'Operazioni e consulenza al cliente',
      description:
        'Holding, conferimenti, PEX, riorganizzazioni e altre operazioni societarie. Ricostruisci il quadro fiscale e societario e trasformalo in un parere o memorandum da condividere.',
    },
  ],
  comparison: {
    title: 'Non tutte le AI sono costruite per il lavoro fiscale e societario',
    text: 'Quando una risposta orienta una scelta fiscale o societaria, deve essere aggiornata, verificabile e utilizzabile nel lavoro professionale.',
    genericLabel: 'AI generalista',
    drawbacks: [
      'Riferimenti a norme e prassi non sempre presenti',
      'Aggiornamento delle fonti non garantito',
      'Fiscalità, prassi e giurisprudenza trattate in un contesto generalista',
      'Analisi e calcoli da adattare al lavoro professionale',
      'Chat separata da bilanci, documenti ed Excel',
    ],
    strengths: [
      'Riferimenti collegati ai documenti originali',
      'Fonti ufficiali sincronizzate ogni giorno',
      'Normativa fiscale, prassi e giurisprudenza tributaria messe in relazione',
      'Pareri, documenti e fogli di calcolo già strutturati',
      'Dai documenti del cliente al risultato in Word o Excel',
    ],
  },
  featuresTitle: 'Le funzionalità più utili nel lavoro fiscale e societario',
  featuredFeatures: [
    {
      slug: 'ricerca-e-quesiti',
      description:
        'Approfondisci quesiti fiscali e societari con normativa, prassi e giurisprudenza verificabili.',
    },
    {
      slug: 'analisi-documentale',
      description:
        'Analizza bilanci, XBRL, dichiarazioni, visure e altri documenti insieme al quesito.',
    },
    {
      slug: 'fogli-calcolo',
      description:
        'Trasforma dati e analisi in fogli con formule modificabili, da continuare in Excel o ODS.',
    },
  ],
  cta: {
    title: 'Portaci il prossimo caso del tuo cliente',
    text: 'Un quesito fiscale, un bilancio, una dichiarazione o un’operazione societaria: prova Trovanorma su qualcosa su cui devi lavorare davvero.',
  },
  testimonial: braccialiTestimonial,
};
